import React, { useState } from 'react'
import { X, CreditCard } from 'lucide-react'
import { useChangeRestaurantPlan } from '@/hooks/admin/useRestaurants'
import { SubscriptionPlan } from '@/types/admin'
import type { RestaurantListDTO } from '@/types/admin'

interface ChangePlanModalProps {
  restaurant: RestaurantListDTO
  onClose: () => void
}

const PLAN_INFO: Record<
  SubscriptionPlan,
  { name: string; price: string; color: string; features: string[] }
> = {
  FREE: {
    name: 'FREE',
    price: '€0/mese',
    color: 'text-gray-600 dark:text-gray-400',
    features: ['Funzionalità base', 'Supporto community', '1 utente'],
  },
  STARTER: {
    name: 'STARTER',
    price: '€29/mese',
    color: 'text-blue-600 dark:text-blue-400',
    features: ['Tutte le funzionalità FREE', 'Supporto email', '3 utenti', 'Analitiche base'],
  },
  PRO: {
    name: 'PRO',
    price: '€79/mese',
    color: 'text-purple-600 dark:text-purple-400',
    features: [
      'Tutte le funzionalità STARTER',
      'Supporto prioritario',
      '10 utenti',
      'Analitiche avanzate',
      'Integrazioni',
    ],
  },
  ENTERPRISE: {
    name: 'ENTERPRISE',
    price: '€199/mese',
    color: 'text-yellow-600 dark:text-yellow-400',
    features: [
      'Tutte le funzionalità PRO',
      'Supporto dedicato',
      'Utenti illimitati',
      'White label',
      'SLA garantito',
    ],
  },
}

export function ChangePlanModal({ restaurant, onClose }: ChangePlanModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(restaurant.currentPlan)
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')

  const changePlanMutation = useChangeRestaurantPlan()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (selectedPlan === restaurant.currentPlan) {
      setError('Seleziona un piano diverso da quello attuale')
      return
    }

    if (!reason.trim()) {
      setError('Inserisci una motivazione per il cambio piano')
      return
    }

    try {
      await changePlanMutation.mutateAsync({
        restaurantId: restaurant.id,
        data: {
          newPlan: selectedPlan,
          reason,
        },
      })
      onClose()
    } catch (error) {
      console.error('Failed to change plan:', error)
      setError('Errore durante il cambio piano')
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Cambia Piano - {restaurant.name}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-6">
              {/* Current Plan */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  Piano attuale:{' '}
                  <span className={`font-semibold ${PLAN_INFO[restaurant.currentPlan].color}`}>
                    {restaurant.currentPlan}
                  </span>
                </p>
              </div>

              {/* Plan Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Seleziona nuovo piano
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(PLAN_INFO).map(([plan, info]) => (
                    <button
                      key={plan}
                      type="button"
                      onClick={() => setSelectedPlan(plan as SubscriptionPlan)}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        selectedPlan === plan
                          ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      } ${
                        restaurant.currentPlan === plan
                          ? 'opacity-50 cursor-not-allowed'
                          : 'cursor-pointer'
                      }`}
                      disabled={restaurant.currentPlan === plan}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-bold text-lg ${info.color}`}>{info.name}</span>
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {info.price}
                        </span>
                      </div>
                      <ul className="space-y-1">
                        {info.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="text-xs text-gray-600 dark:text-gray-400">
                            • {feature}
                          </li>
                        ))}
                      </ul>
                      {restaurant.currentPlan === plan && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                          (Piano corrente)
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Motivazione del cambio *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Es: Cliente richiede upgrade per maggiori funzionalità..."
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Questa motivazione sarà salvata nello storico delle modifiche
                </p>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Warning */}
              {selectedPlan !== restaurant.currentPlan && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                  <p className="text-sm text-orange-800 dark:text-orange-300">
                    Il cambio piano sarà effettivo immediatamente. Il ristorante riceverà una
                    notifica via email.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={changePlanMutation.isPending || selectedPlan === restaurant.currentPlan}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {changePlanMutation.isPending ? 'Cambio in corso...' : 'Conferma Cambio Piano'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
